import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Mail, 
  Phone, 
  Building2, 
  MapPin, 
  Loader2,
  AlertCircle,
  Calendar,
  Clock,
  Briefcase,
  Target,
  Sparkles,
  Linkedin,
  ExternalLink,
  Quote
} from 'lucide-react';

export default function PublicEmployeePage() {
  const { employeeId } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEmployeeData();
  }, [employeeId]);

  const fetchEmployeeData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/employee/${employeeId}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Profil non trouvé');
        } else {
          setError('Erreur lors du chargement du profil');
        }
        return;
      }
      
      const data = await response.json();
      setEmployee(data);
    } catch (err) {
      setError('Impossible de charger le profil');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    if (!employee) return '';
    return `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const calculateSeniority = (hireDate) => {
    if (!hireDate) return null;
    try {
      const hire = new Date(hireDate);
      const today = new Date();
      const years = today.getFullYear() - hire.getFullYear();
      const months = today.getMonth() - hire.getMonth();
      
      if (years === 0) {
        return months <= 1 ? `${months} mois` : `${months} mois`;
      } else if (years === 1 && months < 0) {
        return `${12 + months} mois`;
      } else {
        return years === 1 ? '1 an' : `${years} ans`;
      }
    } catch {
      return null;
    }
  };

  // Dynamic colors from company branding
  const primaryColor = employee?.company?.primaryColor || '#2054A6';
  const accentColor = employee?.company?.accentColor || '#F97316';

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: `${primaryColor}08` }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h1 className="text-xl font-semibold text-slate-700 mb-2">{error}</h1>
            <p className="text-slate-500">
              Ce profil n'est pas disponible ou n'existe pas.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const seniority = calculateSeniority(employee?.hireDate);

  return (
    <div 
      className="min-h-screen py-6 px-4"
      style={{ 
        background: `linear-gradient(180deg, ${primaryColor}12 0%, white 50%)`
      }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header with Company Logo */}
        <div className="text-center mb-6">
          {employee?.company?.logoUrl ? (
            <img 
              src={employee.company.logoUrl} 
              alt={employee.company.name}
              className="h-14 mx-auto object-contain"
            />
          ) : (
            <h2 
              className="text-xl font-bold"
              style={{ color: primaryColor }}
            >
              {employee?.company?.name || 'Entreprise'}
            </h2>
          )}
        </div>

        {/* Main Card - Vignette Style */}
        <Card className="overflow-hidden shadow-xl border-0">
          {/* Top Banner */}
          <div 
            className="h-3"
            style={{ backgroundColor: primaryColor }}
          />
          
          <CardContent className="p-0">
            {/* Profile Section */}
            <div className="p-6 pb-4">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                {/* Photo */}
                <div className="flex-shrink-0">
                  <div 
                    className="p-1 rounded-2xl"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
                  >
                    <Avatar className="h-28 w-28 rounded-xl border-4 border-white">
                      {employee?.photoUrl ? (
                        <AvatarImage 
                          src={employee.photoUrl} 
                          alt={`${employee.firstName} ${employee.lastName}`}
                          className="object-cover rounded-xl"
                        />
                      ) : null}
                      <AvatarFallback 
                        className="text-white text-3xl font-bold rounded-xl"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </div>

                {/* Info */}
                <div className="text-center sm:text-left flex-1">
                  <h1 className="text-2xl font-bold text-slate-900">
                    {employee?.firstName} {employee?.lastName}
                  </h1>
                  <p 
                    className="text-lg font-semibold mt-1"
                    style={{ color: primaryColor }}
                  >
                    {employee?.jobTitle}
                  </p>
                  
                  {/* Organisation badges - Société, Département, Site */}
                  <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                    <Badge 
                      className="gap-1.5 px-3 py-1"
                      style={{ 
                        backgroundColor: `${primaryColor}15`,
                        color: primaryColor,
                        border: `1px solid ${primaryColor}30`
                      }}
                    >
                      <Briefcase className="w-3.5 h-3.5" />
                      {employee?.societe || employee?.company?.name || 'Entreprise'}
                    </Badge>
                    {employee?.department && (
                      <Badge 
                        className="gap-1.5 px-3 py-1"
                        style={{ 
                          backgroundColor: `${accentColor}15`,
                          color: accentColor,
                          border: `1px solid ${accentColor}30`
                        }}
                      >
                        <Building2 className="w-3.5 h-3.5" />
                        {employee.department}
                      </Badge>
                    )}
                    {employee?.site && (
                      <Badge 
                        variant="outline" 
                        className="gap-1.5 px-3 py-1"
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        {employee.site}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="mx-6 border-t border-slate-100" />

            {/* Date & Ancienneté */}
            <div className="px-6 py-4">
              <div className="flex flex-wrap gap-6 justify-center sm:justify-start">
                {employee?.hireDate && (
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}12` }}
                    >
                      <Calendar className="w-4 h-4" style={{ color: primaryColor }} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Date d'entrée</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {formatDate(employee.hireDate)}
                      </p>
                    </div>
                  </div>
                )}
                {seniority && (
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${accentColor}15` }}
                    >
                      <Clock className="w-4 h-4" style={{ color: accentColor }} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Ancienneté</p>
                      <p className="text-sm font-semibold text-slate-800">{seniority}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Parcours / Bio */}
            {employee?.bio && (
              <div className="px-6 pb-4">
                <div 
                  className="p-4 rounded-xl relative"
                  style={{ backgroundColor: `${primaryColor}06` }}
                >
                  <Quote 
                    className="absolute top-2 left-2 w-6 h-6 opacity-20" 
                    style={{ color: primaryColor }}
                  />
                  <div className="pl-6">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: primaryColor }}>
                      Parcours
                    </p>
                    <p className="text-slate-700 text-sm leading-relaxed">
                      {employee.bio}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Valeurs Clés */}
            {employee?.valeursCles && employee.valeursCles.length > 0 && (
              <div className="px-6 pb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Valeurs clés
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {employee.valeursCles.map((valeur, index) => (
                    <Badge 
                      key={index}
                      className="px-3 py-1 text-sm font-medium"
                      style={{ 
                        backgroundColor: `${accentColor}15`,
                        color: accentColor,
                        border: `1px solid ${accentColor}30`
                      }}
                    >
                      {valeur}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Missions */}
            {employee?.missions && employee.missions.length > 0 && (
              <div className="px-6 pb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4" style={{ color: primaryColor }} />
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Missions au quotidien
                  </p>
                </div>
                <div className="space-y-2">
                  {employee.missions.slice(0, 4).map((mission, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-50"
                    >
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {index + 1}
                      </div>
                      <p className="text-sm text-slate-700">{mission}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="mx-6 border-t border-slate-100" />

            {/* Contact Section */}
            <div className="px-6 py-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">
                Contact
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {employee?.email && (
                  <a 
                    href={`mailto:${employee.email}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}12` }}
                    >
                      <Mail className="w-5 h-5" style={{ color: primaryColor }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-sm font-medium text-slate-800 truncate group-hover:text-slate-900">
                        {employee.email}
                      </p>
                    </div>
                  </a>
                )}
                {employee?.phone && (
                  <a 
                    href={`tel:${employee.phone}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}12` }}
                    >
                      <Phone className="w-5 h-5" style={{ color: primaryColor }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-slate-500">Téléphone</p>
                      <p className="text-sm font-medium text-slate-800 group-hover:text-slate-900">
                        {employee.phone}
                      </p>
                    </div>
                  </a>
                )}
              </div>
            </div>

            {/* LinkedIn */}
            {employee?.linkedinUrl && (
              <div className="px-6 pb-6">
                <a 
                  href={employee.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl text-white font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: '#0A66C2' }}
                >
                  <Linkedin className="w-5 h-5" />
                  Voir le profil LinkedIn
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>
            )}

            {/* Footer */}
            <div 
              className="py-3 px-6 text-center"
              style={{ backgroundColor: primaryColor }}
            >
              <p className="text-white/90 text-sm font-medium">
                {employee?.company?.name || 'Entreprise'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-4">
          Fiche générée via QR Code
        </p>
      </div>
    </div>
  );
}
